import {isPlatformBrowser} from '@angular/common';
import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, ViewChild, TemplateRef, ViewEncapsulation} from '@angular/core';
import {TERMS_CONDITIONS_STATUS_LOADED} from '@app/cart/store/selectors/terms-conditions.selectors';
import {SalesOrg, SalesOrgPayload} from '@app/model/dcp-config-service/sales-org';
import {DcpNetNewTermsConditions, DcpTermsAndConditions, DcpTermsAndConditionsDynamicContent} from '@app/model/dcp-terms-and-conditions';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {EddlDownloadAttemptEvent} from '@app/services/analytics/EDDL/events/eddl-download-attempt-event';
import {EddlDownloadSuccessEvent} from '@app/services/analytics/EDDL/events/eddl-download-success-event';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {CONFIGURATION_KEY} from '@app/services/dcp-cart-decorator.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {ConfigService, JsonService, TypeValuePair} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {CmsParagraphComponent, CmsService, EventService, TranslationService} from '@spartacus/core';
import {Product} from '@spartacus/core/src/model/product.model';
import {combineLatest, Subscription} from 'rxjs';
import {first} from 'rxjs/operators';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';

@UntilDestroy()
@Component({
  selector: 'dcp-terms-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TermsAndConditionsComponent implements OnInit, OnDestroy {
  @Input() udexLinkBodySize: string = 'xs';
  @Input() removalStatus: string;
  @Input() entry: OrderEntry;
  @Input() entries: OrderEntry[];
  @Input() isStoreHeaderShown: boolean = false;
  @Input() pdpTerms: { isPdpTerms: boolean; showIconLink: boolean } = {isPdpTerms: false, showIconLink: false};
  @Output() termsConditionsLoadedNoData: EventEmitter<{}> = new EventEmitter<{}>();
  @ViewChild('termsToPrint') netNewTermsPrintContent;
  @ViewChild('modalContentTemplate') modalContentTemplate: TemplateRef<any>;
  @ViewChild('modalFooterTemplate') modalFooterTemplate: TemplateRef<any>;
  modalTitleKey: string = 'productDetails.termsAndConditions';
  modalTitleValue: string;
  entriesToShow: OrderEntry[];
  entryToShow: OrderEntry;
  termsAndConditions: DcpTermsAndConditions;
  termsAndConditionsDynamicContent: DcpTermsAndConditionsDynamicContent;
  termsAndConditionsNetNewEntry: DcpNetNewTermsConditions;
  termsAndConditionsNetNewEntryHtml: string;
  isNetNewTermsConditions: boolean = false;
  salesOrg: SalesOrg;
  modalId: string;

  private rawTermsAndConditionsSubscription: Subscription = Subscription.EMPTY;
  private netNewTermsAndConditionsSubscription: Subscription = Subscription.EMPTY;

  constructor(
    protected cmsService: CmsService,
    public configService: ConfigService,
    private cdr: ChangeDetectorRef,
    private jsonService: JsonService,
    private termsConditionsService: TermsConditionsService,
    private transactionTypeService: TransactionTypeService,
    private translationService: TranslationService,
    private dcpConfigService: DcpConfigService,
    private countrySelectorService: CountrySelectorService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private eventService: EventService,
    private modalService: DefaultModalService,
    @Inject(PLATFORM_ID) public platformId: object,
  ) {
  }

  ngOnInit(): void {
    this.entryToShow = this.entry;
    this.entriesToShow = this.entries;
    if (this.isSapProductPdp(this.entryToShow?.product)) {
      const salesOrgPayload: SalesOrgPayload = {
        vendorId: (this.entryToShow as any)?.vendor?.code || this.entryToShow?.product?.vendor?.code,
        countryIsoCode: this.countrySelectorService.getCurrentCountry().isocode,
        httpParams: {
          default: true
        }
      };
      this.dcpConfigService.getSalesOrg(salesOrgPayload).pipe(untilDestroyed(this)).subscribe(salesOrg => {
        this.salesOrg = salesOrg;
        this.filterOutAbsentTermsEntries();
        this.fetchComponentData(this.entryToShow);
      });
    } else {
      this.filterOutAbsentTermsEntries();
      this.fetchComponentData(this.entryToShow);
    }
  }

  ngOnDestroy(): void {
    this.rawTermsAndConditionsSubscription.unsubscribe();
    this.netNewTermsAndConditionsSubscription.unsubscribe();
  }

  /**
   * Method to parse the Terms and Conditions values.
   * In case an entry has NET_NEW transaction type, fetching its terms conditions from terms-conditions-service instead.
   *
   * @param entry the order entry with product details
   */
  fetchComponentData(entry: OrderEntry) {
    combineLatest([
      this.transactionTypeService.isNetNew(entry?.transactionType),
      this.transactionTypeService.getTransactionTypeConfigurationJson(entry?.transactionType, CONFIGURATION_KEY)
    ]).pipe(first(), untilDestroyed(this))
      .subscribe(([isNetNew, transactionTypeConfig]) => {
        this.isNetNewTermsConditions = isNetNew || this.isSapProductPdp(entry?.product) || entry?.transactionType === TransactionTypeCode.ORDER_RESTRUCTURE_UPGRADE || transactionTypeConfig?.shouldFilterEntitlements;
        if (entry?.data && !this.isNetNewTermsConditions) {
          this.fetchRawTermsAndConditions(entry);
          this.fetchDynamicWcmsComponentContent();
        } else {
          this.termsAndConditions = null;
          if (this.isNetNewTermsConditions) {
            this.fetchNetNewTermsConditionsContent(entry);
          }
        }
      });
  }

  private handleTermsConditionsError(entry) {
    this.entriesToShow = this.entriesToShow.filter(showEntry => showEntry !== entry);
  }

  /**
   * Retrieve raw terms and conditions for non-NET_NEW purchases.
   *
   * @param entry containing the purchase.
   */
  fetchRawTermsAndConditions(entry: OrderEntry) {
    const rawTermsAndConditions: TypeValuePair = entry?.data.find(o => o.type === 'termsAndConditionsParams');
    if (rawTermsAndConditions && rawTermsAndConditions.value) {
      this.termsAndConditions = this.jsonService.parseSafely(rawTermsAndConditions.value) as DcpTermsAndConditions;
    }
  }

  /**
   * Method to parse the dynamic text maintained in the WCMS Component UpsellTermsConditionsComponent
   */
  fetchDynamicWcmsComponentContent() {
    if (this.termsAndConditions?.templateId) {
      this.rawTermsAndConditionsSubscription = this.cmsService.getComponentData(this.termsAndConditions.templateId)
        .pipe(untilDestroyed(this))
        .subscribe((componentData: CmsParagraphComponent) => {
            this.termsAndConditionsDynamicContent = this.jsonService.parseSafely(componentData.content) as DcpTermsAndConditionsDynamicContent;
            this.cdr.detectChanges();
          }
        );
    }
  }

  filterOutAbsentTermsEntries() {
    this.entriesToShow.forEach(entry => {
      if (entry.product?.configurable) {
        this.handleNetNewTermsConditionsContentError(entry);
        return;
      }
      combineLatest([
        this.transactionTypeService.isNetNew(entry?.transactionType),
        this.transactionTypeService.getTransactionTypeConfigurationJson(entry?.transactionType, CONFIGURATION_KEY)
      ]).pipe(first(), untilDestroyed(this))
        .subscribe(([isNetNew, transactionTypeConfig]) => {
          if (isNetNew || this.isSapProductPdp(entry?.product) || entry.transactionType === TransactionTypeCode.ORDER_RESTRUCTURE_UPGRADE || transactionTypeConfig?.shouldFilterEntitlements) {
            this.termsConditionsService.getTermsConditionsByEntryProduct(entry, this.salesOrg).pipe(untilDestroyed(this)).subscribe((termsConditionsMap) => {
              if (termsConditionsMap?.error || (termsConditionsMap?.termsAndConditions?.length && !(termsConditionsMap?.termsAndConditions[0].formattedText))) {
                this.handleNetNewTermsConditionsContentError(entry);
              }
            }, () => {
              this.handleNetNewTermsConditionsContentError(entry);
            });
          }
        });
    });
  }

  /**
   * Fetching NET_NEW terms conditions from terms-conditions-service
   */
  fetchNetNewTermsConditionsContent(entry: OrderEntry) {
    this.netNewTermsAndConditionsSubscription = this.termsConditionsService.getTermsConditionsByEntryProduct(entry, this.salesOrg)
      .pipe(untilDestroyed(this))
      .subscribe(termsConditionsMap => {
        if (!termsConditionsMap?.error) {
          const termsConditions = termsConditionsMap?.termsAndConditions;
          if (Array.isArray(termsConditions) && termsConditions.length && termsConditions[0].formattedText && termsConditionsMap.loadStatus === TERMS_CONDITIONS_STATUS_LOADED) {
            this.termsAndConditionsNetNewEntry = termsConditions[0];
            this.termsAndConditionsNetNewEntryHtml = termsConditions[0].formattedText
              ?.replace(new RegExp('<td class=\"generated-table-even\"><a target=\'_blank\'', 'g'), '<td class=\'body--xxs udex-p-0 udex-pb-16 udex-pb-sm-0 udex-ps-8 udex-ps-sm-20 udex-ps-lg-24 udex-pt-16 udex-pt-sm-20\'><a d class=\'eddl-event-triggering\' target=\'_blank\'')
              .replace(new RegExp('<td class=\"generated-table-odd\">', 'g'), '<td class=\'body--xxs udex-p-0 udex-pb-16 udex-pb-sm-0 udex-pe-8 udex-pe-sm-20 udex-pe-lg-24 udex-pt-16 udex-pt-sm-20\'>')
              .replace(new RegExp('</th><th class=\"generated-table-even\"', 'g'), '</th><th class=\'navigation__bold--m udex-pt-0 udex-pe-0 udex-ps-8 udex-ps-sm-20 udex-pb-8 udex-pb-sm-8 udex-ps-lg-24\'')
              .replace(new RegExp('<th class=\"generated-table-odd\">', 'g'), '<th class=\' navigation__bold--m udex-p-0 udex-pb-8 udex-pb-sm-8\'>')
              .replace(new RegExp('<table>', 'g'), '<table class=\'content-table udex-mt-0 udex-mt-lg-24 udex-mb-12 udex-mb-sm-20\'>')
              .replace(new RegExp('<strong>\\(([a-zA-Z])\\)\\s*</strong>', 'g'), '($1) ')
              .replace(new RegExp('<p>', 'g'), '<p class=\'content-text body--xxs udex-mb-16 udex-mb-sm-20\'>');
            this.cdr.detectChanges();
          } else if (termsConditionsMap.loadStatus === TERMS_CONDITIONS_STATUS_LOADED) {
            this.termsConditionsLoadedNoData.emit(entry);
            this.handleNetNewTermsConditionsContentError(entry);
          }
        } else {
          this.handleNetNewTermsConditionsContentError(entry);
        }
      }, () => {
        this.handleNetNewTermsConditionsContentError(entry);
      });
  }

  private handleNetNewTermsConditionsContentError(entry: OrderEntry) {
    this.handleTermsConditionsError(entry);
    if (this.entryToShow === entry) {
      if (this.entriesToShow.length > 0) {
        this.entryToShow = this.entriesToShow[0];
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Change the Order details values showed in the modal when
   * another order is selected from the dropdown menu inside the modal
   *
   * @param payload the new item showed
   */
  valueChange(payload: string) {
    const entry: OrderEntry = this.entriesToShow?.find(o => o.product?.name === payload);
    this.entryToShow = entry;
    this.fetchComponentData(entry);
  }

  /**
   * Open the T&C modal.
   * Get the t&c data for the entry.
   */
  openTermsAndConditionsModal(): void {
    this.fetchComponentData(this.entryToShow);
    this.translationService.translate(this.modalTitleKey)
      .pipe(first(), untilDestroyed(this))
      .subscribe((translation) => {
        this.modalTitleValue = translation;
      });
    this.modalId = this.modalService.openModal(this.modalContentTemplate, this.modalTitleValue, this.modalFooterTemplate);
  }

  closeTermsAndConditionsModal(): void {
    this.modalService.closeModal(this.modalId);
  }

  addLeadingZeros(inputString: string, targetLength: number): string {
    return inputString.padStart(targetLength, '0');
  }

  /**
   * Fires printing of passed element innerHTML on a new page.
   * Print pre-view is default (depends on User's browser).
   */
  onPrint(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.translationService.translate('register.termsAndConditions')
        .pipe(first(), untilDestroyed(this))
        .subscribe((title) => {
          const printWindow = window.open('');
          printWindow.document.open();
          printWindow.document.write(
            `<head><title>${title}</title></head>
            <body>
              <p>${this.entryToShow?.product?.name} (${this.entryToShow?.product?.supplierAlternativeAID})</p>
              ${this.netNewTermsPrintContent.nativeElement.innerHTML}
            </body>`);
          printWindow.document.close();
          printWindow.print();
        });
    }
  }

  isSapProductPdp(product: Product): boolean {
    return this.pdpTerms.isPdpTerms && this.dcpProductUtilitiesService.isSapProduct(product);
  }

  public triggerEddlDownloadAttemptEvent(clickEvent): void {
    if (clickEvent.target.classList.contains('eddl-event-triggering')) {
      const event = new EddlDownloadAttemptEvent();
      event.url = clickEvent.target.href;
      setTimeout(() => this.eventService.dispatch(event));
    }
  }

  public triggerEddlDownloadSuccessEvent(clickEvent): void {
    if (clickEvent.target.classList.contains('eddl-event-triggering')) {
      const event = new EddlDownloadSuccessEvent();
      event.url = clickEvent.target.href;
      setTimeout(() => this.eventService.dispatch(event));
    }
  }
}
